import {
  useCampaignProgress,
  type CampaignItemStatusKey,
  type CampaignProgress,
  type TradeStatusKey,
} from './progress-queries';
import type { CampaignRecord } from './queries';

import { StatusBar, type StatusBarSegment } from '@/components/status-bar';
import { StatusDot } from '@/components/status-dot';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getBucket } from '@/lib/status-buckets';
import { getLabel } from '@/lib/status-labels';

export interface ProgressPanelProps {
  campaignId: string;
  status: CampaignRecord['status'];
}

const ITEM_KEYS: readonly CampaignItemStatusKey[] = [
  'pending',
  'snapshotted',
  'enqueued',
  'consumed',
  'skipped',
];

const TRADE_KEYS: readonly TradeStatusKey[] = [
  'queued',
  'sending',
  'sent',
  'accepted',
  'declined',
  'expired',
  'cancelled',
  'failed',
];

const formatTimestamp = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('ru-RU') : '—';

const itemSegments = (progress: CampaignProgress): StatusBarSegment[] =>
  ITEM_KEYS.map((key) => ({
    key,
    label: getLabel('campaign-item', key),
    count: progress.items[key],
    bucket: getBucket('campaign-item', key),
  }));

const tradeSegments = (progress: CampaignProgress): StatusBarSegment[] =>
  TRADE_KEYS.map((key) => ({
    key,
    label: getLabel('trade', key),
    count: progress.trades[key],
    bucket: getBucket('trade', key),
  }));

export const ProgressPanel = ({ campaignId, status }: ProgressPanelProps) => {
  const query = useCampaignProgress(campaignId, status);

  if (query.isPending) {
    return (
      <div className="space-y-3 p-6">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex items-center gap-3 p-6 text-sm text-destructive">
        <span>Не удалось загрузить прогресс.</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            void query.refetch();
          }}
        >
          Повторить
        </Button>
      </div>
    );
  }

  const progress = query.data;

  return (
    <div className="space-y-5 p-6">
      <header className="flex flex-wrap items-center gap-4 text-sm">
        <StatusDot domain="campaign" state={progress.status} />
        <span className="text-muted-foreground">
          launched:{' '}
          <span className="text-foreground">{formatTimestamp(progress.timestamps.launchedAt)}</span>
        </span>
        <span className="text-muted-foreground">
          completed:{' '}
          <span className="text-foreground">
            {formatTimestamp(progress.timestamps.completedAt)}
          </span>
        </span>
        <span className="text-muted-foreground">
          cancelled:{' '}
          <span className="text-foreground">
            {formatTimestamp(progress.timestamps.cancelledAt)}
          </span>
        </span>
        {query.isFetching ? (
          <span className="ml-auto text-xs text-muted-foreground">Обновление…</span>
        ) : null}
      </header>

      <section className="space-y-2">
        <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
          <span>Items</span>
          <span>total: {progress.items.total}</span>
        </div>
        <StatusBar
          segments={itemSegments(progress)}
          total={progress.items.total}
          ariaLabel="Items breakdown"
        />
      </section>

      <section className="space-y-2">
        <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
          <span>Trades</span>
          <span>
            accepted/total: {progress.trades.accepted}/{progress.trades.total}
          </span>
        </div>
        <StatusBar
          segments={tradeSegments(progress)}
          total={progress.trades.total}
          ariaLabel="Trades breakdown"
        />
      </section>
    </div>
  );
};
